import { useContext, useEffect, useState } from "react";
import {
  Attribute,
  AttributeGroup,
  AttributeProperty,
  ComponentProps,
  ConfigContext,
  ConfigsProvider,
  PromiseLoader,
} from "react-builder";
import { useProductApi } from "../../custom-hooks/useProductApi";
import { Game } from "../../types/multimedia-types";
import { buildQuery, buildSearchQuery } from "../../utils";
import { MultimediaUserDataContext } from "./MultimediaUserDataProvider";

@AttributeGroup({
  title: "GamesProvider",
  blockName: "GamesProvider",
  module: "multimedia",
  icon: "query",
})
export class GamesProviderAttributes extends Attribute {
  @AttributeProperty({ type: "String" })
  search = "";

  @AttributeProperty({ type: "String" })
  title = "";

  @AttributeProperty({ type: "String" })
  licenseType = "";

  @AttributeProperty({ type: "String" })
  family = "";

  @AttributeProperty({ type: "String" })
  genre = "";

  @AttributeProperty({ type: "String" })
  product = "";

  @AttributeProperty({ type: "String" })
  categories = "";

  @AttributeProperty({ type: "String" })
  shortDescription = "";

  @AttributeProperty({ type: "String" })
  sort = "";

  @AttributeProperty({ type: "String" })
  skip = "";

  @AttributeProperty({ type: "String" })
  limit = "";

  @AttributeProperty({ type: "String" })
  page = "0";

  @AttributeProperty({ type: "String" })
  pageSize = "10";

  @AttributeProperty({ type: "Boolean" })
  checkTotalCount?: boolean;
}

export function GamesProvider(props: ComponentProps<GamesProviderAttributes>) {
  const gamesAPI = useProductApi<Game>();
  const [games, setGames] = useState<Game[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [gamesPromise, setGamesPromise] =
    useState<Promise<{ list: Game[]; totalCount?: number }>>();
  const content = useContext(ConfigContext);
  const { addGameToFavorites, removeGameFromFavorites, favoritesGames } =
    useContext(MultimediaUserDataContext);
  const {
    pageSize,
    sort,
    skip,
    limit,
    page,
    checkTotalCount,
    title,
    family,
    genre,
    product,
    categories,
    licenseType,
    search,
    shortDescription,
  } = props.attributes;
  let parsedTitle = title ? content.getContent(title) || title : title;
  let parsedFamily = family ? content.getContent(family) || family : family;
  let parsedGenre = genre ? content.getContent(genre) || genre : genre;
  let parsedProduct = product ? content.getContent(product) || product : product;
  let parsedCategories = categories ? content.getContent(categories) || categories : categories;
  let parsedLicenseType = licenseType
    ? content.getContent(licenseType) || licenseType
    : licenseType;
  let parsedSearch = search ? content.getContent(search) || search : search;
  let parsedShortDescription = shortDescription
    ? content.getContent(shortDescription) || shortDescription
    : shortDescription;

  useEffect(() => {
    const list = async () => {
      if (!gamesAPI) return;
      const pageInt = parseInt(page);
      const pageSizeInt = parseInt(pageSize);
      // const shouldFetchData = (page || 0) < 2 || games.length < pageInt * pageSizeInt;
      // const checkQueryTotalCount = checkTotalCount && !totalCount;
      const queryfields = {
        title: parsedTitle,
        family: parsedFamily,
        genre: parsedGenre,
        product: parsedProduct,
        categories: parsedCategories,
        licenseType: parsedLicenseType,
        shortDescription: parsedShortDescription,
      };
      let query = {};
      if (parsedSearch) {
        query = buildSearchQuery(parsedSearch, Object.keys(queryfields));
      } else {
        query = buildQuery(queryfields);
      }
      // if (shouldFetchData && (checkTotalCount || totalCount > games.length)) {
      const options = {
        sort,
        skip: skip ? parseInt(skip) : undefined,
        limit: limit ? parseInt(limit) : 25,
        pageSize: pageSizeInt,
        page: page ? pageInt - 1 : undefined,
        totalCount: !!checkTotalCount,
        query,
        populate: "image",
      };

      const filteredOptions = Object.entries(options)
        .filter(([_, value]) => value || value === 0)
        .reduce((obj, [key, value]) => ({ ...obj, [key]: value }), {});
      try {
        const gamesPromise = gamesAPI.list("multimedia/Game", filteredOptions).catch();
        setGamesPromise(gamesPromise);

        const response = await gamesPromise.then();
        setGames(response.list);
        if (response.totalCount) setTotalCount(response.totalCount);
      } catch (error) {
        console.error("Error fetching Games:", error);
      }
      // }
    };

    list();
  }, [
    pageSize,
    sort,
    skip,
    limit,
    page,
    parsedTitle,
    parsedFamily,
    parsedGenre,
    parsedProduct,
    parsedCategories,
    parsedLicenseType,
    parsedSearch,
    parsedShortDescription,
    gamesAPI,
  ]);

  const isFavorite = (gameId: string) => {
    if (!favoritesGames || !favoritesGames.length) return false;
    return !!favoritesGames?.find((g) => g._id === gameId);
  };

  return (
    <PromiseLoader<{ list: Game[]; totalCount?: number }> promise={gamesPromise} loader={<></>}>
      {(games) => (
        <ConfigsProvider
          configJson={{
            games: games?.list
              ? games.list.map((g) => ({
                  ...g,
                  isFavorite: isFavorite(g._id),
                  addGameToFavorites: () => addGameToFavorites(g),
                  removeGameFromFavorites: () => removeGameFromFavorites(g._id),
                }))
              : [],
          }}
        >
          <>{props.children}</>
        </ConfigsProvider>
      )}
    </PromiseLoader>
  );
}

GamesProvider.prototype.Props = GamesProviderAttributes;

export default GamesProvider;
